import "./styles.css";
import shellHtml from "./ui/shell.html?raw";
import { STALE_AFTER_MS, missingConfig } from "./config.ts";
import { backendConfigured, currentSession, signIn as authSignIn, signUp, supabase } from "./auth.ts";
import { needsInstallForOffline } from "./platform/install.ts";
import { requestPersistence } from "./platform/storage.ts";
import { ensureLocalSauna } from "./ui/store.ts";
import { adoptLocalRows, repairBackfilledFlag } from "./db/repo.ts";
import { retryRejected } from "./db/outbox.ts";
import { SyncEngine, autoSync, type SyncState } from "./sync/engine.ts";
import type { Session } from "@supabase/supabase-js";
import { SupabaseTransport } from "./sync/supabase-transport.ts";
import { meta, setMeta } from "./db/idb.ts";
import { capturePendingInvite, redeemPendingInvite } from "./invite.ts";

const root = document.getElementById("boot")!;

/** Set at boot when the app was opened from an invitation link (FR-4.3). */
let invited = false;

/**
 * FR-11.9. This blocks, and it says why. A banner that can be swiped away
 * would be dismissed once and the app would then quietly stop existing
 * between one heating and the next — which is the exact failure it is here
 * to prevent.
 */
function installGate(onContinue: () => void) {
  root.innerHTML = `
    <div class="installgate">
      <h2>Lisää sovellus kotivalikkoon</h2>
      <p>Selaimessa iPhone tyhjentää sovelluksen tiedot noin viikossa. Savusauna
         lämmitetään harvemmin kuin kerran viikossa, joten sovellus ei olisi enää
         siellä kun sitä seuraavan kerran tarvitset.</p>
      <ol>
        <li>Paina jakokuvaketta selaimen alareunassa</li>
        <li>Valitse <strong>Lisää Koti-valikkoon</strong></li>
        <li>Avaa sovellus siitä kuvakkeesta</li>
      </ol>
      <p class="why">Kotivalikkoon lisättynä tiedot säilyvät, sovellus aukeaa
         ilman verkkoa, ja illan kirjaukset odottavat jonossa kunnes kenttää
         taas on.</p>
      <button id="anyway">Jatka silti tällä kertaa</button>
    </div>`;
  document.getElementById("anyway")!.addEventListener("click", onContinue);
}

/**
 * Without a Supabase URL and key the client library throws the moment it is
 * constructed, which used to take the whole boot down and leave a black
 * screen. A missing settings file is the most likely first-run problem there
 * is, so it gets a screen of its own that names the file and the variables.
 */
function setupNeeded(missing: string[], onLocalOnly: () => void) {
  root.innerHTML = `
    <div class="installgate">
      <h2>Asetukset puuttuvat</h2>
      <p>Sovellus ei tiedä mihin Supabase-projektiin se puhuu. Luo tiedosto
         <strong>.env</strong> projektin juureen:</p>
      <pre class="setupcode">cp .env.example .env</pre>
      <p>ja täytä siihen ${missing.map((m) => `<strong>${m}</strong>`).join(" ja ")}.
         Arvot löytyvät Supabase-projektin asetuksista kohdasta API.
         Käynnistä <code>npm run dev</code> uudelleen — Vite lukee .env-tiedoston
         vain käynnistyessään.</p>
      <p class="why">Anon-avain on tarkoitettu selaimeen. Service role -avainta
         ei koskaan laiteta asiakassovellukseen.</p>
      <button id="localonly">Käytä ilman tiliä tällä laitteella</button>
    </div>`;
  document.getElementById("localonly")!.addEventListener("click", onLocalOnly);
}

/** A screen is always drawn, whatever went wrong. */
function fatal(e: unknown) {
  const detail = e instanceof Error ? e.message : String(e);
  console.error(e);
  root.innerHTML = `
    <div class="installgate">
      <h2>Sovellus ei käynnistynyt</h2>
      <p class="why">${detail.replace(/[<&]/g, (c) => (c === "<" ? "&lt;" : "&amp;"))}</p>
      <button onclick="location.reload()">Yritä uudelleen</button>
    </div>`;
}

/**
 * Two modes on one screen: signing in, and creating an account.
 *
 * Creating one asks for a name. It used to take the part before the @ of the
 * email address, which is what everyone else in the sauna then saw against
 * every comment and rating that person wrote — "matti.meikalainen", for good,
 * with nowhere to change it. A name is the one thing a shared archive needs
 * from an account, so it is asked for once, at the only moment the person is
 * already filling in a form.
 */
function signIn(onLocalOnly: () => void, mode: "in" | "up" = invited ? "up" : "in") {
  const up = mode === "up";
  root.innerHTML = `
    <div class="signin">
      <h2>Savusauna</h2>
      ${invited ? `<p class="note" style="margin:0 0 10px">Sinut on kutsuttu saunaan.
         ${up ? "Luo tili, niin liityt siihen heti." : "Kirjaudu, niin liityt siihen heti."}</p>` : ""}
      <input id="email" type="email" inputmode="email" autocomplete="email"
             placeholder="sähköposti" aria-label="sähköposti">
      <input id="pw" type="password" autocomplete="${up ? "new-password" : "current-password"}"
             placeholder="salasana" aria-label="salasana">
      ${up ? `<input id="name" type="text" autocomplete="nickname" maxlength="80"
             placeholder="nimi — tämä näkyy muille" aria-label="nimi">` : ""}
      <button id="${up ? "up" : "in"}">${up ? "Luo tili" : "Kirjaudu"}</button>
      <p class="note" id="msg" role="status"></p>
      <div class="alt">
        <button id="mode" class="link">${up ? "Kirjaudu sen sijaan" : "Luo uusi tili"}</button>
        <button id="localonly" class="link">Jatka ilman tiliä</button>
      </div>
      <p class="note">Ilman tiliä kaikki jää tähän selaimeen: ei varmuuskopiota,
         ei muita laitteita, ei jakamista. Voit kirjautua myöhemmin, ja siihen
         mennessä kirjatut illat tulevat mukana.</p>
    </div>`;

  const msg = document.getElementById("msg")!;
  const value = (id: string) =>
    ((document.getElementById(id) as HTMLInputElement | null)?.value ?? "").trim();
  const busy = (on: boolean) => {
    const b = document.getElementById(up ? "up" : "in") as HTMLButtonElement;
    b.disabled = on;
  };

  document.getElementById("mode")!.addEventListener("click", () => {
    signIn(onLocalOnly, up ? "in" : "up");
  });
  document.getElementById("localonly")!.addEventListener("click", onLocalOnly);

  if (!up) {
    document.getElementById("in")!.addEventListener("click", async () => {
      const email = value("email"), pw =
        (document.getElementById("pw") as HTMLInputElement).value;
      if (!email || !pw) { msg.textContent = "Täytä sähköposti ja salasana."; return; }
      busy(true); msg.textContent = "Kirjaudutaan…";
      try {
        await authSignIn(email, pw);
        await run(await currentSession());
      } catch (e) {
        msg.textContent = e instanceof Error ? e.message : String(e);
        busy(false);
      }
    });
    return;
  }

  document.getElementById("up")!.addEventListener("click", async () => {
    const email = value("email"), name = value("name");
    const pw = (document.getElementById("pw") as HTMLInputElement).value;
    if (!email || pw.length < 6) {
      msg.textContent = "Sähköposti ja vähintään 6 merkin salasana."; return;
    }
    if (!name) { msg.textContent = "Kirjoita nimi — se näkyy muille jäsenille."; return; }
    busy(true); msg.textContent = "Luodaan tiliä…";
    try {
      const { needsConfirmation } = await signUp(email, pw, name);
      if (needsConfirmation) {
        msg.textContent = "Tili luotiin, mutta Supabase odottaa sähköpostivahvistusta. " +
          "Ota Authentication → Sign In / Up → Confirm email pois päältä ja kirjaudu.";
        busy(false); return;
      }
      await run(await currentSession());
    } catch (e) {
      msg.textContent = e instanceof Error ? e.message : String(e);
      busy(false);
    }
  });
}

/**
 * FR-11.4 and FR-11.11, plus the thing they left out: saying when it worked.
 *
 * A bar that only appears when something is wrong cannot be trusted, because
 * an empty bar means both "everything arrived" and "nothing happened, and I
 * did not notice". So a sync that actually delivered something says so, for
 * a few seconds, and then gets out of the way.
 */
let confirmTimer: ReturnType<typeof setTimeout> | undefined;
let engineRef: SyncEngine | null = null;

function renderSyncBar(s: SyncState, lastSync: string | null) {
  const bar = document.getElementById("syncbar") as HTMLElement | null;
  const msg = document.getElementById("syncmsg");
  if (!bar || !msg) return;
  const stale = lastSync ? Date.now() - Date.parse(lastSync) > STALE_AFTER_MS : true;
  clearTimeout(confirmTimer);

  bar.className = "syncbar";
  bar.onclick = null;
  let text = "";
  let transient = false;

  if (s.rejected > 0) {
    // Data that is on the device and will not go up. It outranks everything
    // else the bar could say, because only the user can resolve it — and
    // tapping is how they try again once whatever refused it has changed.
    bar.classList.add("queued", "tappable");
    text = `${s.rejected} kirjausta ei kelvannut — kokeile uudelleen ›`;
    bar.onclick = async () => {
      await retryRejected();
      void engineRef?.run();
    };
  } else if (s.running && s.queued > 0) {
    text = `Lähetetään ${s.queued}…`;
  } else if (s.last_error && s.queued > 0) {
    /* Not every failure is a lost network. Saying so for all of them sends
       people to check their signal when the server answered and refused the
       row. The reason it gave is the only useful thing here. */
    bar.classList.add("queued");
    text = `${s.queued} odottaa — ${reason(s.last_error)}`;
    bar.title = s.last_error;
  } else if (s.queued > 0) {
    bar.classList.add("queued");
    text = `${s.queued} kirjausta odottaa verkkoa`;
  } else if (!s.online) {
    bar.classList.add("offline");
    text = "Ei verkkoa — kirjaukset tallentuvat laitteelle";
  } else if (s.superseded.length) {
    /* Delivered and beaten. The user typed a value that is not the one that
       stands, and until this branch existed nothing said so. Transient: the
       newer value is already coming down in the pull, so there is nothing
       left to do about it and nothing to nag about. */
    const fields = [...new Set(s.superseded)].join(", ");
    bar.classList.add("synced");
    text = `Ohitettu: ${fields} — palvelimella oli uudempi arvo`;
    transient = true;
  } else if (s.pushed > 0) {
    bar.classList.add("synced");
    text = `Synkronoitu ${clock(lastSync)} · ${s.pushed} ${s.pushed === 1 ? "kirjaus" : "kirjausta"}`;
    transient = true;
  } else if (stale) {
    bar.classList.add("stale");
    text = "Ei synkronoitu vähään aikaan — avaa sovellus verkossa ennen saunalle lähtöä";
  } else if (lastSync) {
    bar.classList.add("synced");
    text = `Ajan tasalla ${clock(lastSync)}`;
    transient = true;
  }

  msg.textContent = text;
  bar.hidden = text === "";
  if (transient) confirmTimer = setTimeout(() => { bar.hidden = true; }, 5000);
}

/** The server's own words, shortened to something that fits a bar. */
function reason(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes("failed to fetch") || m.includes("networkerror") ||
      m.includes("load failed")) return "ei saatu yhteyttä";
  if (m.includes("row-level security")) return "palvelin ei hyväksynyt riviä (käyttöoikeus)";
  if (m.includes("foreign key")) return "viittaus puuttuu palvelimelta";
  if (m.includes("duplicate key")) return "rivi on jo palvelimella";
  if (m.includes("jwt") || m.includes("token") || m.includes("401") ||
      m.includes("unauthorized")) return "kirjautuminen vanhentunut";
  if (m.includes("invalid path")) return "väärä Supabase-osoite";
  const first = raw.split(":").pop()?.trim() ?? raw;
  return first.length > 48 ? first.slice(0, 45) + "…" : first;
}

function clock(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => (n < 10 ? "0" : "") + n;
  return `klo ${p(d.getHours())}.${p(d.getMinutes())}`;
}

async function start(): Promise<void> {
  await requestPersistence();                       // FR-11.10

  /* Before the Supabase client is constructed, so the token is out of the
     address bar whatever happens next, and before any screen is drawn, so the
     sign-in screen can say what it is for. */
  invited = (await capturePendingInvite()) !== null;

  if (!backendConfigured()) {
    setupNeeded(missingConfig(), () => void run(null));
    return;
  }
  const session = await currentSession();
  if (!session) { signIn(() => void run(null)); return; }
  await run(session);
}

async function run(session: Session | null): Promise<void> {
  root.innerHTML = shellHtml;
  applySmokePreference();
  pauseSmokeWhenHidden();

  /* Nothing here waits for a network. The app opens from what is on the
     device — that is the whole of FR-11.1, and an awaited request in the
     boot path quietly breaks it: at the sauna the request does not fail
     fast, it hangs, and the app never draws. The sync fills in the rest
     once it has something to talk to. */
  const local = await ensureLocalSauna("Savusauna");
  let sauna_id = (await meta<string>("sauna_id")) ?? local.sauna_id;

  /* Rows an earlier build wrote in a shape the server refuses. If any were
     repaired, the ones already set aside deserve another go: they were only
     set aside because of the shape that has just been fixed. */
  if (await repairBackfilledFlag()) await retryRejected();

  let name = session?.user.user_metadata?.["display_name"] as string | undefined;
  name = name ?? session?.user.email ?? (await meta<string>("local_name")) ?? "Minä";
  if (!session) await setMeta("local_name", name);

  /* Anything recorded before this account existed belongs to it now. Without
     this the server rejects those rows for having no author, and the queue
     stops at the first one and never moves again. */
  if (session) await adoptLocalRows(session.user.id, name);

  /* An invitation is redeemed here, between signing in and the first sync,
     and nowhere else. The engine pushes this device's own sauna to the server
     as soon as it finds the account has none — run after it, and the invited
     person lands in an empty sauna of their own instead of the shared one. */
  let joinFailed: string | null = null;
  if (session) {
    const joined = await redeemPendingInvite();
    if (joined?.sauna_id) {
      sauna_id = joined.sauna_id;
      await setMeta("sauna_id", sauna_id);
      invited = false;
    } else if (joined?.error) {
      joinFailed = joined.error;
    }
  }

  const app = await import("./ui/app.js");

  /* Rows written before signing in carry no author id — the columns are
     nullable for exactly this. The name is kept on the row itself, so a
     comment written offline still reads as someone's. */
  await app.init({
    user_id: session?.user.id ?? null,
    display_name: name,
    sauna_id,
    stove_id: local.stove_id,
    lang: "fi",
  });

  if (joinFailed) app.notice(joinFailed);

  if (!session) {
    renderLocalOnlyBar();
    return;                       // nothing to sync with, and nothing to warn about
  }

  const engine = new SyncEngine(new SupabaseTransport(supabase()), sauna_id);
  engineRef = engine;
  engine.subscribe((s) => {
    void meta<string>("last_sync_at").then((t) => renderSyncBar(s, t ?? null));
  });
  engine.subscribe((s) => { if (!s.running) void app.reload(); });
  autoSync(engine);
}

/**
 * Local-only mode has to be leavable. It was not: the choice was offered once
 * at the sign-in screen and then had no way back short of clearing the site's
 * data — which would have taken the evenings with it. Whatever was recorded
 * without an account comes along at sign-in.
 */
function renderLocalOnlyBar() {
  const bar = document.getElementById("syncbar") as HTMLElement | null;
  const msg = document.getElementById("syncmsg");
  if (!bar || !msg) return;
  bar.className = "syncbar offline tappable";
  msg.textContent = "Vain tällä laitteella — kirjaudu jakaaksesi ›";
  bar.hidden = false;
  bar.onclick = () => signIn(() => void run(null));
}

/**
 * A deployed change has to actually arrive.
 *
 * The service worker takes over as soon as it installs, but the page keeps
 * running the code it started with — so the first visit after a deploy shows
 * the old app and the fix looks like it never shipped. One reload, once, when
 * a new worker takes control. The guard matters: the very first install has
 * no previous controller and must not reload.
 */
function reloadOnNewVersion(): void {
  if (!("serviceWorker" in navigator)) return;
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading || !navigator.serviceWorker.controller) return;
    reloading = true;
    location.reload();
  });
  const check = () => {
    if (document.visibilityState !== "visible") return;
    void navigator.serviceWorker.getRegistration().then((r) => r?.update());
  };
  document.addEventListener("visibilitychange", check);
  check();
}

/** The smoke stops while nobody is looking at it. */
function pauseSmokeWhenHidden(): void {
  const set = () => {
    const el = document.getElementById("smoke");
    if (el) el.classList.toggle("paused", document.visibilityState !== "visible");
  };
  document.addEventListener("visibilitychange", set);
  set();
}

/** A display preference, held on the device it applies to. */
function applySmokePreference(): void {
  let off = false;
  try { off = localStorage.getItem("savusauna.smoke") === "off"; } catch { /* private mode */ }
  document.body.classList.toggle("no-smoke", off);
}

reloadOnNewVersion();

const boot = () => start().catch(fatal);
if (needsInstallForOffline()) installGate(boot);
else void boot();
